import { Location } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { pathFromUrl } from '../../utils/platform-route.util';

@Injectable({
  providedIn: 'root',
})
export class PlatformSectionSpyService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly activeByPath = signal<Record<string, string>>({});

  /**
   * Returns the active section id for a route path.
   * @param path - Route path such as `/profile`
   * @returns Section id or null
   */
  sectionFor(path: string): string | null {
    return this.activeByPath()[path] ?? null;
  }

  /**
   * Stores the active section id for a route path and mirrors it into the URL hash.
   * @param path - Route path such as `/profile`
   * @param sectionId - Visible section id
   * @param options - Sync options
   * @param options.syncUrl - When false, only updates sidenav state
   */
  setSection(
    path: string,
    sectionId: string,
    options: { syncUrl?: boolean } = {}
  ): void {
    this.activeByPath.update((current) => {
      if (current[path] === sectionId) {
        return current;
      }
      return { ...current, [path]: sectionId };
    });
    if (options.syncUrl === false) {
      return;
    }
    this.syncUrlFragment(path, sectionId);
  }

  /**
   * Updates the browser URL fragment without triggering a Router navigation.
   * @param path - Route path that owns the section
   * @param sectionId - Section id to write as hash
   */
  private syncUrlFragment(path: string, sectionId: string): void {
    if (pathFromUrl(this.router.url) !== path) {
      return;
    }
    const browserFragment = this.location.path(true).split('#')[1] ?? null;
    if (browserFragment === sectionId) {
      return;
    }
    const urlTree = this.router.parseUrl(this.router.url);
    urlTree.fragment = sectionId;
    this.location.replaceState(this.router.serializeUrl(urlTree));
  }
}
